import { useEffect, useState } from 'react'
import { Paperclip, FileText, X } from 'lucide-react'
import type { CommentAttachment } from '@/types'

const ACCEPT = 'image/jpeg,image/png,image/gif,image/webp,application/pdf'
const MAX_FILES = 5
const MAX_SIZE = 10 * 1024 * 1024

interface AttachmentPickerProps {
  files: File[]
  onFilesChange: (files: File[]) => void
  existing?: CommentAttachment[]
  deletedIds?: number[]
  onDeletedIdsChange?: (ids: number[]) => void
  label?: string
  compact?: boolean
}

export function AttachmentPicker({
  files,
  onFilesChange,
  existing = [],
  deletedIds = [],
  onDeletedIdsChange,
  label = 'Joindre un fichier',
  compact = false
}: AttachmentPickerProps) {
  const [previews, setPreviews] = useState<{ name: string; type: string; url?: string }[]>([])
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const newPreviews = files.map(f => ({
      name: f.name,
      type: f.type,
      url: f.type.startsWith('image/') ? URL.createObjectURL(f) : undefined
    }))
    setPreviews(newPreviews)
    return () => {
      newPreviews.forEach(p => { if (p.url) URL.revokeObjectURL(p.url) })
    }
  }, [files])

  const visibleExisting = existing.filter(a => !deletedIds.includes(a.id))
  const totalCount = files.length + visibleExisting.length

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setError(null)
    const selected = Array.from(e.target.files || [])

    const oversized = selected.find(f => f.size > MAX_SIZE)
    if (oversized) {
      setError(`"${oversized.name}" dépasse 10 Mo`)
      e.target.value = ''
      return
    }

    const merged = [...files, ...selected]
    const allowed = Math.max(0, MAX_FILES - visibleExisting.length)
    if (merged.length > allowed) {
      setError(`5 fichiers maximum (${visibleExisting.length} existant${visibleExisting.length > 1 ? 's' : ''})`)
      onFilesChange(merged.slice(0, allowed))
    } else {
      onFilesChange(merged)
    }
    e.target.value = ''
  }

  const removeNewFile = (index: number) => {
    onFilesChange(files.filter((_, i) => i !== index))
  }

  const removeExisting = (id: number) => {
    if (onDeletedIdsChange) {
      onDeletedIdsChange([...deletedIds, id])
    }
  }

  const canAdd = totalCount < MAX_FILES

  return (
    <div className="space-y-2">
      {(visibleExisting.length > 0 || previews.length > 0) && (
        <div className="flex flex-wrap gap-2">
          {visibleExisting.map(att => (
            <div key={`existing-${att.id}`} className="relative group">
              {att.content_type.startsWith('image/') ? (
                <img src={att.url} alt={att.filename} className="h-16 w-16 object-cover rounded-lg border border-gray-200" />
              ) : (
                <div className="h-16 w-16 flex items-center justify-center rounded-lg border border-gray-200 bg-gray-50">
                  <FileText className="w-6 h-6 text-gray-400" />
                </div>
              )}
              {onDeletedIdsChange && (
                <button
                  type="button"
                  onClick={() => removeExisting(att.id)}
                  className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                  title="Supprimer"
                >
                  <X className="w-3 h-3" />
                </button>
              )}
              <p className="text-xs text-gray-500 truncate w-16 mt-0.5">{att.filename}</p>
            </div>
          ))}
          {previews.map((preview, index) => (
            <div key={`new-${index}`} className="relative group">
              {preview.url ? (
                <img src={preview.url} alt={preview.name} className="h-16 w-16 object-cover rounded-lg border border-gray-200" />
              ) : (
                <div className="h-16 w-16 flex items-center justify-center rounded-lg border border-gray-200 bg-gray-50">
                  <FileText className="w-6 h-6 text-gray-400" />
                </div>
              )}
              <button
                type="button"
                onClick={() => removeNewFile(index)}
                className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                title="Retirer"
              >
                <X className="w-3 h-3" />
              </button>
              <p className="text-xs text-gray-500 truncate w-16 mt-0.5">{preview.name}</p>
            </div>
          ))}
        </div>
      )}

      <div className="flex items-center gap-3 flex-wrap">
        <label className={`inline-flex items-center gap-2 px-3 py-1.5 text-sm rounded-lg cursor-pointer transition-colors ${
          canAdd
            ? 'text-gray-600 hover:text-tessan-primary hover:bg-gray-100'
            : 'text-gray-300 cursor-not-allowed'
        }`}>
          <Paperclip className="w-4 h-4" />
          {label}
          <input
            type="file"
            accept={ACCEPT}
            multiple
            disabled={!canAdd}
            onChange={handleFileChange}
            className="hidden"
          />
        </label>
        {!compact && (
          <span className="text-xs text-gray-400">
            {totalCount}/{MAX_FILES} · JPEG, PNG, GIF, WebP, PDF · 10 Mo max
          </span>
        )}
      </div>

      {error && <p className="text-sm text-red-600">{error}</p>}
    </div>
  )
}

export function AttachmentList({ attachments }: { attachments: CommentAttachment[] }) {
  if (!attachments || attachments.length === 0) return null

  return (
    <div className="flex flex-wrap gap-2">
      {attachments.map(att => (
        att.content_type.startsWith('image/') ? (
          <a
            key={att.id}
            href={att.url}
            target="_blank"
            rel="noopener noreferrer"
            className="block"
          >
            <img
              src={att.url}
              alt={att.filename}
              className="max-h-40 rounded-lg border border-gray-200 hover:border-tessan-primary transition-colors"
            />
          </a>
        ) : (
          <a
            key={att.id}
            href={att.url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm text-gray-700 hover:border-tessan-primary hover:text-tessan-primary transition-colors"
          >
            <FileText className="w-4 h-4" />
            {att.filename}
          </a>
        )
      ))}
    </div>
  )
}
